import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import Bugsnag, { isBugsnagInitialized } from "./utils/bugsnag.js";
import { AppLoading } from "./components/app-loading/AppLoading.js";
import React from "react";
import { Route, Routes } from "react-router-dom";
import { ConfigProvider } from "antd";
import { omniTheme } from "./theme/omniTheme.js";
import { Watch } from "./components/spinner/Spinner.js";
// @ts-expect-error - antd locale types may not be available in all environments
import enUS from "antd/locale/en_US";
import { CampaignStateProvider } from "./hooks/useCampaignState.jsx";
// Configure dayjs: Monday-first, ISO 8601 week numbers (used by every DatePicker)
import "./utils/dayjsLocale.js";

// Custom locale with Monday as first day of week
// In Ant Design 5, firstDayOfWeek should be at the DatePicker level
const customLocale = {
  ...enUS,
  DatePicker: {
    ...(enUS.DatePicker || {}),
    firstDayOfWeek: 1, // Monday (0 = Sunday, 1 = Monday)
  },
};

// Lazy load the main backoffice page
const BackofficeApp = lazy(() => import("./pages/index.js"));

// Error fallback component as recommended in official docs
const ErrorFallback = () => {
  return (
    <div
      style={{
        padding: "20px",
        textAlign: "center",
        fontFamily: "Arial, sans-serif",
        color: "#666",
      }}
    >
      <h2>Something went wrong</h2>
      <p>
        We're sorry, but something unexpected happened. Please try refreshing the
        page.
      </p>
      <button
        onClick={() => {
          window.location.reload();
        }}
        style={{
          padding: "10px 20px",
          backgroundColor: "#007bff",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
        }}
      >
        Refresh Page
      </button>
    </div>
  );
};

// Create error boundary using Bugsnag React plugin
// Only create if Bugsnag is properly initialized
// Per official docs: https://docs.bugsnag.com/platforms/javascript/react/
const createErrorBoundary = () => {
  // Early return if Bugsnag hasn't been initialized
  // This prevents the "getPlugin() was called before start()" error
  if (!isBugsnagInitialized || !Bugsnag) {
    return null;
  }

  // Check if Bugsnag has been started by verifying getPlugin exists
  // getPlugin is only available after Bugsnag.start() is called
  if (typeof Bugsnag.getPlugin !== 'function') {
    return null;
  }

  // Try to get the React plugin
  // Since we've verified Bugsnag is initialized, this should work
  try {
    const reactPlugin = Bugsnag.getPlugin("react");
    
    if (reactPlugin && typeof reactPlugin.createErrorBoundary === "function") {
      return reactPlugin.createErrorBoundary(React);
    }
  } catch {
    // Fallback in case something unexpected happens
    return null;
  }

  return null;
};

// Simple React Error Boundary as fallback if Bugsnag is not available
class SimpleErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ComponentType },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ComponentType }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const Fallback = this.props.fallback;
      return <Fallback />;
    }
    return this.props.children;
  }
}

// Create QueryClient outside component to ensure it persists across re-renders
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  // Try to create ErrorBoundary when component renders
  const ErrorBoundary = createErrorBoundary();
  
  // Always render the main content, with or without ErrorBoundary
  const mainContent = (
    <ConfigProvider locale={customLocale} theme={omniTheme} spin={{ indicator: <Watch size={48} /> }}>
      <QueryClientProvider client={queryClient}>
        <CampaignStateProvider>
          <Suspense fallback={<AppLoading />}>
            <Routes>
              <Route path="/*" element={<BackofficeApp />} />
            </Routes>
          </Suspense>
        </CampaignStateProvider>
      </QueryClientProvider>
    </ConfigProvider>
  );

  // If ErrorBoundary is not available, use simple React ErrorBoundary
  if (!ErrorBoundary) {
    return (
      <SimpleErrorBoundary fallback={ErrorFallback}>
        {mainContent}
      </SimpleErrorBoundary>
    );
  }

  // Render with Bugsnag ErrorBoundary
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      {mainContent}
    </ErrorBoundary>
  );
}

export default App;
